import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from './PortalLayout';
import { employeeV2Service } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import MobileBanner from '../../components/common/MobileBanner';

// ─── Helpers ────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = ['#2563eb', '#2563eb', '#7c3aed', '#d97706', '#0891b2', '#dc2626', '#0d9488'];
const avatarColor = (name = '') => AVATAR_PALETTE[(name.charCodeAt(0) || 0) % AVATAR_PALETTE.length];
const initials = (name = '') => name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

const PAY_FREQ_KEYS = { daily: 'daily', weekly: 'weekly', monthly: 'monthly' };
const PAY_TYPE_KEYS = { salary: 'salary', hourly: 'hourly', hybrid: 'hybrid' };

const StatusBadge = ({ status, t }) => {
    const active = (status || '').toLowerCase() === 'active';
    return (
        <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: active ? '#eff5ff' : '#f1f5f9', color: active ? '#166534' : '#64748b' }}>
            {active ? t('active') : t('inactive')}
        </span>
    );
};

const CashierBadge = ({ t }) => (
    <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 5, padding: '2px 8px', background: '#eff5ff', color: '#2563eb' }}>
        {t('cashier')}
    </span>
);

const Avatar = ({ name, size = 36 }) => (
    <div style={{ width: size, height: size, borderRadius: '50%', background: avatarColor(name), display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: size * 0.36, fontWeight: 700, flexShrink: 0 }}>
        {initials(name)}
    </div>
);

const payDisplay = (comp, currency, t) => {
    if (!comp || comp.base_amount == null) return '—';
    const amount = Number(comp.base_amount || 0).toFixed(2);
    const freq = comp.pay_frequency ? t(PAY_FREQ_KEYS[comp.pay_frequency] || comp.pay_frequency) : '';
    return freq ? `${currency}${amount} / ${freq}` : `${currency}${amount}`;
};

// ─── Empty State ─────────────────────────────────────────────────────────────

const EmptyState = ({ filtered, onAdd, t }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', color: '#94a3b8' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16, opacity: 0.4 }}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: '#475569' }}>
            {filtered ? t('no_employees_match_filters') : t('no_employees_yet')}
        </p>
        <p style={{ margin: '0 0 20px', fontSize: 13 }}>
            {filtered ? t('try_adjusting_search_or_filter') : t('add_first_team_member')}
        </p>
        {!filtered && (
            <button onClick={onAdd} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {t('add_employee')}
            </button>
        )}
    </div>
);

// ─── Add Employee Modal (nested v2 payload) ──────────────────────────────────

const DEPARTMENT_KEYS = ['kitchen', 'front_of_house', 'bar', 'management', 'delivery', 'other'];
const DEPARTMENT_VALUES = ['Kitchen', 'Front of House', 'Bar', 'Management', 'Delivery', 'Other'];
const EMPLOYMENT_TYPES = [{ value: 'full_time', key: 'full_time_label' }, { value: 'part_time', key: 'part_time_label' }, { value: 'contractor', key: 'contractor' }];
const PAY_TYPES = [{ value: 'salary', key: 'salary' }, { value: 'hourly', key: 'hourly' }, { value: 'hybrid', key: 'hybrid' }];
const PAY_FREQUENCIES = [{ value: 'daily', key: 'daily' }, { value: 'weekly', key: 'weekly' }, { value: 'monthly', key: 'monthly' }];

const BLANK_FORM = {
    name: '', role: '', status: 'Active', department: '',
    phone: '', email: '', preferred_language: 'en',
    employment_type: '', start_date: '',
    pay_type: 'salary', base_amount: '', pay_frequency: 'monthly',
    is_cashier: false, pin: '', system_role: 'staff',
};

const inputStyle = { width: '100%', border: '1px solid #e2e8f0', borderRadius: 7, padding: '9px 12px', fontSize: 13, color: '#212529', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 };

const AddEmployeeModal = ({ onSave, onClose, saving, serverError, t }) => {
    const [form, setForm] = useState(BLANK_FORM);
    const [showPin, setShowPin] = useState(false);
    const [error, setError] = useState('');

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSubmit = () => {
        if (!form.name.trim()) return setError(t('name_is_required'));
        if (!form.role.trim()) return setError(t('role_is_required'));
        if (!form.pin) return setError(t('pin_required_for_new'));
        if (!/^\d{6}$/.test(form.pin)) return setError(t('pin_must_be_6_digits'));
        setError('');

        const payload = {
            name: form.name.trim(),
            role: form.role.trim(),
            department: form.department || null,
            status: form.status,
            personal: {
                phone: form.phone || null,
                email: form.email || null,
                preferred_language: form.preferred_language || 'en',
            },
            employment: form.employment_type || form.start_date ? {
                employment_type: form.employment_type || 'full_time',
                start_date: form.start_date || null,
            } : null,
            compensation: {
                pay_type: form.pay_type || 'salary',
                base_amount: parseFloat(form.base_amount) || 0,
                pay_frequency: form.pay_frequency || 'monthly',
            },
            access: {
                is_cashier: !!form.is_cashier,
                pin_code: form.pin,
                // POS privilege level. Without this the backend defaults every
                // employee to 'staff', and the POS manager gate — which tests
                // the JWT role claim minted from system_role — is unpassable
                // for everyone, so no one can configure a station.
                system_role: form.system_role || 'staff',
            },
        };
        onSave(payload);
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="portal-modal-card" style={{ background: 'white', borderRadius: 14, width: 560, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onKeyDown={e => e.key === 'Escape' && onClose()}>

                <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#212529' }}>{t('add_employee')}</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, borderRadius: 4, display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {(error || serverError) && <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 7, padding: '10px 14px', fontSize: 13, color: '#e11d48' }}>{error || serverError}</div>}

                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div>
                            <label style={labelStyle}>{t('name')} *</label>
                            <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder={t('full_name')} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('role')} *</label>
                            <input style={inputStyle} value={form.role} onChange={e => set('role', e.target.value)} placeholder={t('role_placeholder')} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('department')}</label>
                            <select style={inputStyle} value={form.department} onChange={e => set('department', e.target.value)}>
                                <option value="">{t('select_department')}</option>
                                {DEPARTMENT_VALUES.map((d, i) => <option key={d} value={d}>{t(DEPARTMENT_KEYS[i])}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('employment_type')}</label>
                            <select style={inputStyle} value={form.employment_type} onChange={e => set('employment_type', e.target.value)}>
                                <option value="">{t('select_type')}</option>
                                {EMPLOYMENT_TYPES.map(et => <option key={et.value} value={et.value}>{t(et.key)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('phone')}</label>
                            <input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+44 7700 000000" />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('email')}</label>
                            <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="name@example.com" />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('start_date')}</label>
                            <input style={inputStyle} type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('status')}</label>
                            <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                                <option value="Active">{t('active')}</option>
                                <option value="Inactive">{t('inactive')}</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('pay_type')}</label>
                            <select style={inputStyle} value={form.pay_type} onChange={e => set('pay_type', e.target.value)}>
                                {PAY_TYPES.map(pt => <option key={pt.value} value={pt.value}>{t(pt.key)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('base_amount')}</label>
                            <input style={inputStyle} type="number" value={form.base_amount} onChange={e => set('base_amount', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('pay_frequency')}</label>
                            <select style={inputStyle} value={form.pay_frequency} onChange={e => set('pay_frequency', e.target.value)}>
                                {PAY_FREQUENCIES.map(pf => <option key={pf.value} value={pf.value}>{t(pf.key)}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('pin')} *</label>
                            <div style={{ position: 'relative' }}>
                                <input style={{ ...inputStyle, paddingRight: 40 }} type={showPin ? 'text' : 'password'} value={form.pin} onChange={e => set('pin', e.target.value)} placeholder={t('six_digit_pin')} maxLength={6} />
                                <button type="button" onClick={() => setShowPin(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {showPin ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div style={{ background: form.is_cashier ? '#eff5ff' : '#f8fafc', border: `1px solid ${form.is_cashier ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s' }}
                        onClick={() => set('is_cashier', !form.is_cashier)}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{t('cashier_access')}</div>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('can_login_via_pin')}</div>
                        </div>
                        <div style={{ width: 40, height: 22, borderRadius: 11, background: form.is_cashier ? '#2563eb' : '#cbd5e1', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                            <div style={{ position: 'absolute', top: 3, left: form.is_cashier ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                        </div>
                    </div>

                    {/* POS access level. Separate from the Role field above,
                        which is a free-text job title the POS never reads. The
                        station-settings gate tests the JWT role claim, minted
                        from system_role — so a "Manager" left on Staff here
                        cannot configure a till. */}
                    <div>
                        <label style={labelStyle}>{t('pos_access_level')}</label>
                        <select
                            style={inputStyle}
                            value={form.system_role}
                            onChange={e => set('system_role', e.target.value)}
                        >
                            <option value="staff">{t('pos_access_staff')}</option>
                            <option value="manager">{t('pos_access_manager')}</option>
                        </select>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                            {form.system_role === 'manager'
                                ? t('pos_access_manager_hint')
                                : t('pos_access_staff_hint')}
                        </div>
                    </div>

                    <div>
                        <label style={labelStyle}>{t('preferred_language')}</label>
                        <select value={form.preferred_language || 'en'} onChange={e => set('preferred_language', e.target.value)} style={inputStyle}>
                            <option value="en">{t('english')}</option>
                            <option value="ar">{t('arabic')}</option>
                        </select>
                    </div>
                </div>

                <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 7, padding: '9px 18px', fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
                        {t('cancel')}
                    </button>
                    <button onClick={handleSubmit} disabled={saving} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 7, padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, transition: 'background 0.15s' }}
                        onMouseEnter={e => { if (!saving) e.currentTarget.style.background = '#1d4ed8'; }} onMouseLeave={e => { if (!saving) e.currentTarget.style.background = '#2563eb'; }}>
                        {saving ? t('saving') : t('add_employee')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Mobile Employee Card ───────────────────────────────────────────────────

const MobileEmployeeRow = ({ emp, currency, onOpen, t }) => {
    const isActive = (emp.status || '').toLowerCase() === 'active';
    const subtitleParts = [emp.role, emp.department].filter(Boolean);
    const detailRow = (label, value) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
            <span style={{ color: '#64748b' }}>{label}</span>
            <span style={{ fontWeight: 600, color: '#212529', textAlign: 'right' }}>{value}</span>
        </div>
    );
    return (
        <div onClick={() => onOpen(emp.employee_id)} style={{ cursor: 'pointer' }}>
            <MobileCard
                avatar={{ initials: initials(emp.name), tone: isActive ? 'green' : 'gray' }}
                title={emp.name}
                amount={payDisplay(emp.compensation, currency, t)}
                status={{ label: isActive ? t('active') : t('inactive'), tone: isActive ? 'green' : 'gray' }}
                subtitle={subtitleParts.join(' · ')}
            >
                {detailRow(t('role'), emp.role || '—')}
                {detailRow(t('department'), emp.department || '—')}
                {detailRow(t('pay'), payDisplay(emp.compensation, currency, t))}
                {emp.access?.is_cashier && detailRow(t('cashier_access'), t('yes') || 'Yes')}
                {emp.personal?.phone && detailRow(t('phone'), emp.personal.phone)}
                {emp.personal?.email && detailRow(t('email'), emp.personal.email)}
            </MobileCard>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PortalEmployeesPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { getCurrency } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();
    const currency = getCurrency();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterDept, setFilterDept] = useState('all');
    const [filterFrequency, setFilterFrequency] = useState('all');
    const [filterCashier, setFilterCashier] = useState(false);

    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await employeeV2Service.listEmployees();
            setEmployees(Array.isArray(data) ? data : []);
        } catch {
            setError(t('failed_to_load_employees'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (location.state?.openAddModal) setShowForm(true);
    }, [location.state]);

    const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
    const activeCount = employees.filter(e => (e.status || '').toLowerCase() === 'active').length;
    const cashierCount = employees.filter(e => e.access?.is_cashier).length;

    const filtered = employees.filter(e => {
        if (search && !e.name.toLowerCase().includes(search.toLowerCase()) && !(e.role || '').toLowerCase().includes(search.toLowerCase())) return false;
        if (filterStatus !== 'all' && (e.status || '').toLowerCase() !== filterStatus) return false;
        if (filterDept !== 'all' && e.department !== filterDept) return false;
        if (filterFrequency !== 'all' && e.compensation?.pay_frequency !== filterFrequency) return false;
        if (filterCashier && !e.access?.is_cashier) return false;
        return true;
    });

    const openAdd = () => { setSaveError(''); setShowForm(true); };

    const handleSave = async (payload) => {
        try {
            setSaving(true);
            setSaveError('');
            await employeeV2Service.createEmployee(payload);
            setShowForm(false);
            await load();
        } catch (err) {
            const msg = err?.response?.data?.detail || t('failed_to_save_employee');
            setSaveError(msg);
        } finally {
            setSaving(false);
        }
    };

    const filterBtnStyle = (active) => ({
        fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', border: '1px solid',
        background: active ? '#2563eb' : 'white', color: active ? 'white' : '#475569', borderColor: active ? '#2563eb' : '#e2e8f0',
        transition: 'all 0.15s',
    });

    return (
        <PortalLayout>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#212529' }}>{t('employees')}</h2>
                        <p style={{ margin: '3px 0 0', fontSize: 13, color: '#64748b' }}>{employees.length} {employees.length !== 1 ? t('team_members') : t('team_member')}</p>
                    </div>
                    <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#1d4ed8'} onMouseLeave={e => e.currentTarget.style.background = '#2563eb'}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('add_employee')}
                    </button>
                </div>

                {isMobile && !loading && employees.length > 0 && (
                    <>
                        <MobileHero
                            label={t('employees')}
                            value={`${employees.length}`}
                            meta={`${employees.length !== 1 ? t('team_members') : t('team_member')}`}
                            stats={[
                                { label: t('active'), value: `${activeCount}`, tone: 'green' },
                                { label: t('inactive'), value: `${employees.length - activeCount}` },
                                { label: t('cashier'), value: `${cashierCount}` },
                            ]}
                        />
                        {cashierCount === 0 && (
                            <MobileBanner
                                tone="warn"
                                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M2 10h20" /></svg>}
                                title={t('no_cashiers_yet') || 'No cashiers yet'}
                                subtitle={t('enable_cashier_access_hint') || 'Enable cashier access to let staff log in via PIN'}
                            />
                        )}
                    </>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search_by_name_or_role')} style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px 8px 36px', fontSize: 13, color: '#212529', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    </div>

                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[{ key: 'all', label: t('all_status') }, { key: 'active', label: t('active') }, { key: 'inactive', label: t('inactive') }].map(o => (
                            <button key={o.key} onClick={() => setFilterStatus(o.key)} style={filterBtnStyle(filterStatus === o.key)}>{o.label}</button>
                        ))}
                    </div>

                    {departments.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button onClick={() => setFilterDept('all')} style={filterBtnStyle(filterDept === 'all')}>{t('all_departments')}</button>
                            {departments.map(d => (
                                <button key={d} onClick={() => setFilterDept(d)} style={filterBtnStyle(filterDept === d)}>{d}</button>
                            ))}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[{ key: 'all', label: t('all_pay_frequencies') }, ...PAY_FREQUENCIES.map(pf => ({ key: pf.value, label: t(pf.key) }))].map(o => (
                            <button key={o.key} onClick={() => setFilterFrequency(o.key)} style={filterBtnStyle(filterFrequency === o.key)}>{o.label}</button>
                        ))}
                    </div>

                    <button onClick={() => setFilterCashier(v => !v)} style={filterBtnStyle(filterCashier)}>
                        {t('cashier')}
                    </button>
                </div>

                {error && <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#e11d48', marginBottom: 16 }}>{error}</div>}

                <div style={{ flex: 1, display: 'flex', background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', minHeight: 0 }}>

                    <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
                        {loading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: '#94a3b8', fontSize: 13 }}>{t('loading')}</div>
                        ) : filtered.length === 0 ? (
                            <EmptyState filtered={search || filterStatus !== 'all' || filterDept !== 'all' || filterFrequency !== 'all' || filterCashier} onAdd={openAdd} t={t} />
                        ) : isMobile ? (
                            <div style={{ padding: 12 }}>
                                {filtered.map(emp => (
                                    <MobileEmployeeRow
                                        key={emp.employee_id}
                                        emp={emp}
                                        currency={currency}
                                        onOpen={(id) => navigate(`/portal/employees/${id}`)}
                                        t={t}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="portal-table-wrap">
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        {[t('employee'), t('role'), t('department'), t('pay'), t('status'), ''].map((h, i) => (
                                            <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(emp => (
                                        <tr key={emp.employee_id}
                                            onClick={() => navigate(`/portal/employees/${emp.employee_id}`)}
                                            style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer', transition: 'background 0.1s' }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                            <td style={{ padding: '12px 16px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <Avatar name={emp.name} size={32} />
                                                    <div>
                                                        <div style={{ fontSize: 13, fontWeight: 600, color: '#212529' }}>{emp.name}</div>
                                                        {emp.access?.is_cashier && <div style={{ marginTop: 2 }}><CashierBadge t={t} /></div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{emp.role}</td>
                                            <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{emp.department || '—'}</td>
                                            <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{payDisplay(emp.compensation, currency, t)}</td>
                                            <td style={{ padding: '12px 16px' }}><StatusBadge status={emp.status} t={t} /></td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="9 18 15 12 9 6"/>
                                                </svg>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showForm && (
                <AddEmployeeModal
                    onSave={handleSave}
                    onClose={() => { setShowForm(false); setSaveError(''); }}
                    saving={saving}
                    serverError={saveError}
                    t={t}
                />
            )}
        </PortalLayout>
    );
};

export default PortalEmployeesPage;
